import React from 'react'
import { useTranslation } from 'react-i18next'

import { BaseModalType } from '@context/ModalsContext'

import { Input } from '@ui/components/Input'
import { ListItem } from '@ui/components/List'
import { Modal } from '@ui/components/Modal'
import { Loader } from '@ui/components/Spinner/Loader'
import { CloseIcon } from '@ui/icons/Close.icon'
import { SearchIcon } from '@ui/icons/Search.icon'

import { AddressAutocompleteSuggestion } from '@model/AddressAutocomplete'

import { Box } from '@shared/components/Box'

interface AddressLookupMobileModalProps {
  value: string
  isLoading: boolean
  suggestions: AddressAutocompleteSuggestion[]
  showManualOption: boolean
  onChange: (value: string) => void
  onClear: () => void
  onSelectSuggestion: (suggestion: AddressAutocompleteSuggestion) => Promise<void> | void
  onSelectManualEntry: () => void
}

export const AddressLookupMobileModal: BaseModalType<AddressLookupMobileModalProps> = (props) => {
  const {
    close,
    value,
    isLoading,
    suggestions,
    showManualOption,
    onChange,
    onClear,
    onSelectSuggestion,
    onSelectManualEntry,
  } = props
  const { t } = useTranslation()

  const handleSuggestionClick = (suggestion: AddressAutocompleteSuggestion) => {
    void Promise.resolve(onSelectSuggestion(suggestion)).finally(() => {
      close()
    })
  }

  const handleManualEntryClick = () => {
    onSelectManualEntry()
    close()
  }

  return (
    <Modal>
      <Modal.Header>{t('Sign up.Address Line 1')}</Modal.Header>
      <Modal.Body>
        <Input
          fullWidth
          showLabel={false}
          label={t('Sign up.Search or enter your address')}
          aria-label={t('Sign up.Search or enter your address')}
          placeholder={t('Sign up.Search or enter your address')}
          value={value}
          variant='search'
          size='lg'
          autoFocus
          leftIcon={SearchIcon}
          rightIcon={!isLoading && value ? CloseIcon : undefined}
          renderRightIconJSX={isLoading ? <Loader size={20} color='text-high-em' /> : undefined}
          onEndIconClick={value ? onClear : undefined}
          onChange={(event) => onChange(event.target.value)}
        />

        {(suggestions.length > 0 || showManualOption) && (
          <Box flex direction='column' gap='x4' fullWidth>
            {suggestions.map((suggestion) => (
              <ListItem
                key={suggestion.placeId}
                size='lg'
                onClick={() => handleSuggestionClick(suggestion)}
                subtitle={suggestion.secondaryText}
              >
                {suggestion.mainText || suggestion.text}
              </ListItem>
            ))}
            {showManualOption && (
              <ListItem onClick={handleManualEntryClick}>{`${t(
                "Sign up.Can't find your address?"
              )} ${t('Sign up.Enter manually')}`}</ListItem>
            )}
          </Box>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary onClick={close}>{t('Close')}</Modal.Footer.Secondary>
      </Modal.Footer>
    </Modal>
  )
}
